import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Field, FieldStateTypes, FieldTypes, PlanStatuses } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { REQUIRED_TEXT } from '@/common/constants';
import { AccountToPushToField } from '@/components/elements/AccountToPushToField';
import Label from '@/components/elements/Label';
import { useCurrentOrganization } from '@/modules/core';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import useBanksListQuery from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/BanksList/useBanksList.query';
import { useGetInternalSubAccountNumberQuery } from '@/modules/employer/components/SetupEmployer/queries/useGetInternalSubAccountNumber.query';
import { useCurrentPlan } from '@/modules/plan/PlanSetup/hooks';
import usePlanSetupFieldsSourceState from '@/modules/plan/PlanSetup/hooks/usePlanSetupFieldsSourceState';
import { PLAN_SETUP_FIELD } from '@/modules/plan/PlanSetup/PlanSetup.constants';
import theme from '@/styles/theme';

import { BILLING_METHOD_OPTIONS, BillingMethod, FUNDING_METHODS, FUNDING_METHODS_OPTIONS } from '../AccountFunding.types';
import { useAccountFundingParamsStore, useFinancialDetailsParamsStore } from '../stores';

import { useFundingMethodsFeatureFlag } from './useFundingMethodsFeatureFlag';

const HSA_PLAN_TYPE = 'HSA';
const ACCOUNT_PLACEHOLDER = 'The account used to push funds for contribution will be provided once organization is activated.';

const useFinancialDetailsParamsFields = (isEditMode?: boolean, isActivationMode?: boolean, isTemplateMode?: boolean) => {
  const ref = useRef(false);
  const currentState = useFinancialDetailsParamsStore((state) => state.state);
  const handleSetState = useFinancialDetailsParamsStore((state) => state.setState);
  const [planLevelFundingState, setPlanLevelFundingState] = useState(currentState?.planLevelFunding ?? false);
  const [isPreFundCalculatedState, setIsPreFundCalculatedState] = useState(currentState?.isPreFundCalculated ?? false);
  const [initValuesSet, setInitValuesSet] = useState(false);

  const sourceState = usePlanSetupFieldsSourceState();
  const isFundingMethodsFF = useFundingMethodsFeatureFlag();
  const {
    observingOrganizationData,
    observingOrganizationNormalProperties,
    observingPartnerNormalProperties,
  } = useOrganizationOverridableProperties();

  const { observingOrganization: { id: orgId } } = useCurrentOrganization();

  const { options: bankOptions  } = useBanksListQuery(orgId || '');

  const pushContributionAccountId = useMemo(
    () => observingOrganizationNormalProperties?.contributionClearingAccount || observingPartnerNormalProperties?.contributionClearingAccount,
    [observingOrganizationNormalProperties?.contributionClearingAccount, observingPartnerNormalProperties?.contributionClearingAccount]
  );

  const {
    accountNumber: contributionClearingAccountNumber,
    routingNumber: contributionClearingRoutingNumber,
  } = useGetInternalSubAccountNumberQuery(pushContributionAccountId);

  const orgAllowPrefund = useMemo(() => observingOrganizationData?.allowPrefund ?? false, [observingOrganizationData]);

  const disableByState = useCallback((fieldName: string) => {
    const currentValue = _.get(sourceState, `${fieldName}`) as FieldStateTypes;
    return currentValue === FieldStateTypes.Locked
        || currentValue === FieldStateTypes.Hidden;
  }, [sourceState]);


  const { planStatus, planType } = useCurrentPlan();

  const isDraftOrReadyForSetupStatus = useMemo(
    () => planStatus === PlanStatuses.DRAFT || planStatus === PlanStatuses.READY_FOR_SETUP,
  [planStatus]);

  const fundingMethod = useAccountFundingParamsStore((state) => state.state.fundingMethod);
  const sourceFundingMethod = useAccountFundingParamsStore((state) => state.sourceState?.fundingMethod);
  const errorFields = useAccountFundingParamsStore((state) => state.errorFields);
  const billingMethodTypeId = useAccountFundingParamsStore((state) => state.state.billingMethodTypeId);

  const fundingFieldsValues = useCallback(() => {
      let planLevelFunding = currentState?.planLevelFunding ?? false;
      let isPreFundCalculated = currentState?.isPreFundCalculated ?? false;
      if (fundingMethod === FUNDING_METHODS.STANDARD_NOTIONAL) {
        planLevelFunding = false;
        isPreFundCalculated = false;
      }
      if (fundingMethod === FUNDING_METHODS.PREFUND_SPENDDOWN) {
        planLevelFunding = true;
        isPreFundCalculated = true;
      }
      if (fundingMethod === FUNDING_METHODS.PREFUND_AND_CONTRIBUTION) {
        planLevelFunding = true;
        isPreFundCalculated = true;
      }
      if (fundingMethod === FUNDING_METHODS.CONTRIBUTION_ONLY) {
        planLevelFunding = false;
        isPreFundCalculated = false;
      }
      return [planLevelFunding, isPreFundCalculated];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fundingMethod]);
  
  const [planLevelFunding, isPreFundCalculated] = useMemo(() => fundingFieldsValues(), [fundingFieldsValues]);

  const handlePlanLevelFundingChange = useCallback((val: boolean) => {
    setPlanLevelFundingState(val);
  }, []);

  const handleIsPreFundCalculatedChange = useCallback((val: boolean) => {
    setIsPreFundCalculatedState(val);
  }, []);

  const currentMethodOptions = useMemo(() => {
    if (!orgAllowPrefund) {
      return [
        FUNDING_METHODS_OPTIONS[0],
        {
          divide: true,
          isGroupDivider: true,
          groupKey: 'Other',
          groupDividerTitle: 'These configs are unavailable because Prefund is currently disabled for this organization.',
          groupDividerTitleStyle: {
            color: theme?.colors?.danger || 'red',
            fontSize: '14px',
            lineHeight: '20px',
          },
        },
        {
          key: FUNDING_METHODS.PREFUND_SPENDDOWN,
          value: 'Prefund Spenddown',
          disabled: true,
        },
        {
          key: FUNDING_METHODS.PREFUND_AND_CONTRIBUTION,
          value: 'Prefund and Contribution',
          disabled: true,
          divide: true,
        },
        FUNDING_METHODS_OPTIONS[3]
      ];
    }
    return FUNDING_METHODS_OPTIONS;
  }, [orgAllowPrefund]);

  useEffect(() => {
    setPlanLevelFundingState(currentState?.planLevelFunding ?? false);
    setIsPreFundCalculatedState(currentState?.isPreFundCalculated ?? false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSetState({
      ...currentState,
      planLevelFunding: planLevelFundingState,
      isPreFundCalculated: isPreFundCalculatedState,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLevelFundingState, isPreFundCalculatedState]);

  useEffect(() => {
    if (fundingMethod !== sourceFundingMethod && !ref.current) {
      setInitValuesSet(true);
      ref.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingMethod]);

  useEffect(() => {
    if (!initValuesSet) {
      return;
    }
    setPlanLevelFundingState(planLevelFunding);
    setIsPreFundCalculatedState(isPreFundCalculated);
  }, [planLevelFunding, isPreFundCalculated, initValuesSet]);

  const billingFields = useMemo(() => {    
    const fields: Field[] = [
      {
        name: 'billingMethodTypeId',
        type: FieldTypes.Radio,
        label: 'Method',
        options: BILLING_METHOD_OPTIONS,
        showRequireIcon: true,
        disabled: !isEditMode,
        validator: isActivationMode ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING,
      },
    ];

    if (billingMethodTypeId === BillingMethod.PULL) {
      fields.push({
        name: 'externalBankAccount',
        type: FieldTypes.Dropdown,
        disabled: !isEditMode,
        validator: isTemplateMode ? VALIDATORS.STRING : VALIDATORS.REQUIRED_STRING,
        placeholder: 'Select bank account',
        singleMode: true,
        label: 'Bank account',
        options: bankOptions,
        showRequireIcon: !isTemplateMode,
      });
    }

    if (billingMethodTypeId === BillingMethod.PUSH) {
      fields.push(
        {
          name: 'accountToPushTo',
          type: FieldTypes.Node,
          label: 'Account to push to',
          value: (
            <AccountToPushToField
              accountNumber={contributionClearingAccountNumber}
              routingNumber={contributionClearingRoutingNumber}
              testId="plan-billing-account-to-push"
              accountPlaceholder={ACCOUNT_PLACEHOLDER}
            />
          ),
        },
        {
          name: 'externalPushReturnAccount',
          type: FieldTypes.Dropdown,
          disabled: !isEditMode,
          validator: isTemplateMode ? VALIDATORS.STRING : VALIDATORS.REQUIRED_STRING,
          placeholder: 'Select bank account',
          singleMode: true,
          label: <Label title="Bank account for refunds" desc="Used solely to return credits or refunds. This account will not be debited." />,
          options: bankOptions,
          showRequireIcon: !isTemplateMode,
        }
      );
    }

    return fields;
  }, [billingMethodTypeId, isEditMode, bankOptions, contributionClearingAccountNumber, contributionClearingRoutingNumber, isActivationMode, isTemplateMode]);

  const fields = useMemo(() => {
    return [
    {
      name: 'fundingMethod',
      type: FieldTypes.Dropdown,
      label: 'Funding Method',
      options: currentMethodOptions,
      placeholder: 'Select option',
      showRequireIcon: true,
      singleMode: true,
      ...isFundingMethodsFF ? { validator: yup.string().test({
        test: (val) => val !== `${errorFields?.fundingMethod?.value}`,
        message: errorFields?.fundingMethod?.message,
      })
      .test({
        test: (val) => {
          if (planType === HSA_PLAN_TYPE) {
            return val === `${FUNDING_METHODS.CONTRIBUTION_ONLY}`;
          }
          return true;
        },
        message: 'Funding Method must be Contribution Only for plan type \'HSA\'.',
      })
      .required(REQUIRED_TEXT),
    } : {},
      disabled: !isDraftOrReadyForSetupStatus,
    },
    {
      name: 'planLevelFunding',
      type: FieldTypes.Node,
      label: 'Plan level funding',
      value: (
        <Box>
          <Checkbox
            id="planLevelFunding"
            data-testId="planLevelFunding"
            name={PLAN_SETUP_FIELD.PLAN_LEVEL_FUNDING}
            onChange={handlePlanLevelFundingChange}
            checked={planLevelFundingState}
            disabled={
              fundingMethod === FUNDING_METHODS.PREFUND_SPENDDOWN
              || fundingMethod === FUNDING_METHODS.PREFUND_AND_CONTRIBUTION
              || !isEditMode
              || disableByState(PLAN_SETUP_FIELD.PLAN_LEVEL_FUNDING)
            }
          />
        </Box>
      ),
    },
    ...(planLevelFundingState ? billingFields : []),
    ...(fundingMethod !== FUNDING_METHODS.CONTRIBUTION_ONLY) ? [{
      name: 'isPreFundCalculated',
      type: FieldTypes.Node,
      label: 'Custom prefund calculation',
      value: (
        <Box>
          <Checkbox
            id="isPreFundCalculated"
            data-testId="isPreFundCalculated"
            name={PLAN_SETUP_FIELD.IS_PRE_FUND_CALCULATED}
            onChange={handleIsPreFundCalculatedChange}
            checked={isPreFundCalculatedState}
            disabled={
              fundingMethod === FUNDING_METHODS.PREFUND_SPENDDOWN
              || !isEditMode
              || disableByState(PLAN_SETUP_FIELD.IS_PRE_FUND_CALCULATED)
            }
          />
        </Box>
      ),
    }] : []
  ] as Field[];
  },
  [
    errorFields,
    fundingMethod,
    planLevelFundingState,
    handlePlanLevelFundingChange,
    handleIsPreFundCalculatedChange,
    isPreFundCalculatedState,
    isEditMode,
    isDraftOrReadyForSetupStatus,
    disableByState,
    planType,
    isFundingMethodsFF,
    currentMethodOptions,
    billingFields,
  ]);

  return fields;
};

export default useFinancialDetailsParamsFields;
